(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root && typeof root === 'object') {
    root.ToeeProPackage = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  /**
   * Dealer Pro tire ⇄ wheel package (dealer-pro docs/superpowers/plans/
   * 2026-09-11-tire-wheel-package.md). WHICH wheel fits a tire is decided by
   * the app (GET {base}/fitment/rims|tires, which refuses any SKU whose tags
   * disagree); this file only carries those answers into a package and
   * re-checks the two halves against each other when the dealer swaps one.
   * The width rule below is a copy of the app's api/utils/fitment.ts and is
   * pinned by the same table in tests/toee-pro-package-logic.test.js.
   */

  var RESULT_CAP = 20; // POST /pro/quote-batch MAX_VARIANTS
  var TIRE_SIZE = /^(LT)?(\d{3})[\s/.-]?(\d{2})[\s/.-]?Z?R?(\d{2})(C)?(?!\d)/;

  function parseTireSize(str) {
    var m = TIRE_SIZE.exec(String(str == null ? '' : str).trim().toUpperCase());
    if (!m) return null;
    var widthMm = Number(m[2]);
    var aspect = Number(m[3]);
    var rimIn = Number(m[4]);
    return { widthMm: widthMm, aspect: aspect, rimIn: rimIn, commercial: !!(m[1] || m[5]), size: widthMm + '/' + aspect + 'R' + rimIn };
  }

  function rimWidthRangeIn(widthMm) {
    var half = function (x) { return Math.round(x * 2) / 2; };
    return { min: half((widthMm * 0.7) / 25.4), max: half((widthMm * 0.9) / 25.4) };
  }

  function queryString(params) {
    var parts = [];
    Object.keys(params || {}).forEach(function (key) {
      var v = params[key];
      if (v === null || v === undefined || String(v) === '') return;
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(String(v)));
    });
    return parts.length ? '?' + parts.join('&') : '';
  }

  function fitmentRimsUrl(base, params) {
    return String(base || '') + '/fitment/rims' + queryString(params);
  }

  function fitmentTiresUrl(base, params) {
    return String(base || '') + '/fitment/tires' + queryString(params);
  }

  function fitmentFromRims(json) {
    var out = { ids: [], byId: {} };
    ((json && json.matches) || []).slice(0, RESULT_CAP).forEach(function (m) {
      var id = String(m.variantId);
      out.ids.push(id);
      out.byId[id] = { kind: 'rim', spec: m.spec };
    });
    return out;
  }

  function fitmentFromTires(json) {
    var out = { ids: [], byId: {} };
    ((json && json.matches) || []).slice(0, RESULT_CAP).forEach(function (m) {
      var id = String(m.variantId);
      out.ids.push(id);
      out.byId[id] = { kind: 'tire', size: m.size, odMm: m.odMm, deltaPct: m.deltaPct };
    });
    return out;
  }

  function emptyPackage() {
    return { tire: null, rim: null };
  }

  function setPackageSide(pkg, side, quote) {
    var next = { tire: pkg ? pkg.tire : null, rim: pkg ? pkg.rim : null };
    next[side] = quote || null;
    return next;
  }

  function removePackageSide(pkg, side) {
    return setPackageSide(pkg, side, null);
  }

  function tireSizeOf(quote) {
    var stamped = quote && quote.fitment && quote.fitment.size;
    return parseTireSize(stamped || (quote && quote.title));
  }

  function packageConflicts(pkg) {
    if (!pkg || !pkg.tire || !pkg.rim) return [];
    var tire = tireSizeOf(pkg.tire);
    var spec = pkg.rim.fitment && pkg.rim.fitment.spec;
    if (!tire || !spec) return ['unknown'];
    var out = [];
    if (tire.rimIn !== spec.diameterIn) {
      out.push('diameter');
    } else {
      var range = rimWidthRangeIn(tire.widthMm);
      if (spec.widthIn < range.min || spec.widthIn > range.max) out.push('width');
    }
    // Both halves were priced by quote-batch at a quantity; the card can only
    // be honest if it is the same one (see buildQuoteCardViewModel's own note
    // on requestedQuantity being the single source of "how many").
    if ((pkg.tire.requestedQuantity || 1) !== (pkg.rim.requestedQuantity || 1)) out.push('quantity');
    return out;
  }

  function packageReady(pkg) {
    return !!(pkg && pkg.tire && pkg.rim && pkg.tire.price && pkg.rim.price && packageConflicts(pkg).length === 0);
  }

  function buildPackageQuote(pkg) {
    return {
      kind: 'package',
      variantId: pkg.tire.variantId,
      rimVariantId: pkg.rim.variantId,
      title: pkg.tire.title || '',
      rimTitle: pkg.rim.title || '',
      requestedQuantity: pkg.tire.requestedQuantity || 1,
      tire: pkg.tire,
      rim: pkg.rim
    };
  }

  function formatPcd(pcd) {
    return String(pcd == null ? '' : pcd).replace('x', '×');
  }

  function formatInches(n) {
    return Number(n).toFixed(1);
  }

  // ---------------------------------------------------------------------
  // Browser wiring. Methods are public so tests can drive them on the fake
  // DOM; handleClick only maps a click to one of them. Every string shown is
  // read from a data-i18n-* attribute rendered by Liquid (Task 9).
  // ---------------------------------------------------------------------

  function i18n(el, name) {
    return (el && el.getAttribute && el.getAttribute('data-i18n-' + name)) || '';
  }

  function PackageController(doc, deps) {
    deps = deps || {};
    this.doc = doc;
    this.search = deps.search || null;
    this.base = deps.proxyBase || '/apps/pro';
    this.fetchImpl = deps.fetchImpl || null;
    this.vehicle = deps.vehicle || null;
    // The Wheels tab's own vehicle lookup (Task 7): another instance of the
    // Tires lookup's controller, so a pick here never starts a tire search.
    this.wheelVehicle = deps.wheelVehicle || null;
    this.pkg = emptyPackage();
    this.kind = 'all';
    this.diameter = null;
    this.forTire = null;
    this.requestId = 0;
    // Which Find fitments / list load the answer on screen belongs to, and which
    // request (and retry) a fitment chip's own wheel search is.
    this._fitmentAsk = 0;
    this._fitmentChoice = 0;
    this._chainAsk = 0;
    this._chipAsk = null;
    this._chipRetry = null;
    this.finder = doc.querySelector('[data-pro-wheel-finder]');
    this.bar = doc.querySelector('[data-pro-package-bar]');
  }

  PackageController.prototype._q = function (sel) {
    return this.doc.querySelector(sel);
  };

  PackageController.prototype._show = function (sel, on) {
    var el = this._q(sel);
    if (el) el.hidden = !on;
  };

  PackageController.prototype._make = function (tag, className, text) {
    var node = this.doc.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  function toggleClass(el, name, on) {
    var list = String(el.className || '').split(/\s+/).filter(function (c) { return c && c !== name; });
    if (on) list.push(name);
    el.className = list.join(' ');
  }

  /** "Wheels that fit {size}": the value is drawn in bold. Only a tire context
   * can be cleared (Clear drops the tire, see clearContext). An empty
   * template hides the line. */
  PackageController.prototype._setContext = function (template, token, value, clearable) {
    var on = !!template;
    this._show('[data-pro-finder-context-row]', on);
    this._show('[data-pro-finder-clear]', on && !!clearable);
    var el = this._q('[data-pro-finder-context]');
    if (!el) return;
    el.hidden = !on;
    el.replaceChildren();
    if (!on) return;
    var self = this;
    String(template).split(token).forEach(function (bit, i) {
      if (i) el.appendChild(self._make('b', '', value));
      if (bit) el.appendChild(self._make('span', '', bit));
    });
  };

  /** One finder message at a time, as the Status family draws it (rollout
   * Task 3): 'error' is the bordered callout with Try again, 'invalid' a
   * marker line that also marks the OE field, anything else the quiet
   * sentence with the call-to-transfer link. '' clears it. */
  PackageController.prototype._status = function (str, kind) {
    var oe = this._q('[data-pro-oe-input]');
    if (oe) {
      toggleClass(oe, 'tt-pro-input--error', !!str && kind === 'invalid');
      oe.setAttribute('aria-invalid', String(!!str && kind === 'invalid'));
    }
    var el = this._q('[data-pro-finder-status]');
    if (!el) return;
    el.hidden = !str;
    el.replaceChildren();
    if (!str) return;
    var box;
    var line;
    if (kind === 'error') {
      box = this._make('div', 'tt-pro-callout tt-pro-callout--error');
      line = box.appendChild(this._make('p', 'tt-pro-callout__message', str));
      line.setAttribute('role', 'alert');
      var retry = box.appendChild(this._make('button', 'tt-pro-btn2', i18n(this.finder, 'retry') || 'Try again'));
      retry.type = 'button';
      retry.setAttribute('data-pro-finder-retry', '');
    } else if (kind === 'invalid') {
      box = this._make('div', 'tt-pro-state-row');
      box.appendChild(this._make('span', 'tt-pro-marker tt-pro-marker--error')).setAttribute('aria-hidden', 'true');
      line = box.appendChild(this._make('p', 'tt-pro-note tt-pro-state-row__text', str));
      line.setAttribute('role', 'alert');
    } else {
      box = this._make('div', 'tt-pro-state');
      line = box.appendChild(this._make('p', 'tt-pro-state__text', str));
      line.setAttribute('role', 'status');
      var phoneEl = this._q('[data-i18n-call-phone]');
      var phone = phoneEl && phoneEl.getAttribute('data-i18n-call-phone');
      var call = i18n(this.finder, 'call-transfer');
      if (phone && call) box.appendChild(this._make('a', 'tt-pro-link', call)).setAttribute('href', 'tel:' + phone);
    }
    el.appendChild(box);
  };

  /** Still placeholder chips while /fitment/rims is in flight (Wheels-states D). */
  PackageController.prototype._loadingChips = function (sel) {
    var box = this._q(sel);
    if (!box) return;
    box.replaceChildren();
    for (var i = 0; i < 6; i++) {
      box.appendChild(this._make('span', 'tt-pro-skeleton tt-pro-finder__skeleton')).setAttribute('aria-hidden', 'true');
    }
  };

  /** A failed lookup: the message, and no skeleton left standing. A row left
   * empty takes its label with it (only the error callout stays); the
   * diameter group comes back when its load runs again (_loadDiameters). */
  PackageController.prototype._fail = function (message, kind) {
    this._status(message, kind);
    var self = this;
    ['diameter', 'pcd', 'fitment'].forEach(function (name) {
      var box = self._q('[data-pro-' + name + '-chips]');
      if (!box || !box.querySelector('.tt-pro-finder__skeleton')) return;
      box.replaceChildren();
      self._show('[data-pro-' + name + '-group]', false);
      if (name === 'diameter') self._diameterOff = true;
    });
  };

  PackageController.prototype._press = function (attr, value) {
    var els = this.doc.querySelectorAll('[' + attr + ']');
    for (var i = 0; i < els.length; i++) {
      els[i].setAttribute('aria-pressed', String(els[i].getAttribute(attr) === String(value)));
    }
  };

  PackageController.prototype._chips = function (containerSel, attr, options, label) {
    var box = this._q(containerSel);
    if (!box) return;
    box.replaceChildren();
    for (var i = 0; i < options.length; i++) {
      var b = this.doc.createElement('button');
      b.type = 'button';
      b.className = 'tt-pro-chip';
      b.setAttribute(attr, String(options[i].value));
      b.setAttribute('aria-pressed', 'false');
      b.textContent = label(options[i]);
      box.appendChild(b);
    }
  };

  /** One finder request at a time: an older answer arriving after a newer
   * request is dropped (same requestId pattern as ProSearch#_search).
   * `retry` re-runs the whole step; Try again calls it. */
  PackageController.prototype._getJson = function (url, retry) {
    var self = this;
    var id = ++this.requestId;
    this._retry = retry || null;
    this._status('');
    if (!this.fetchImpl) return Promise.resolve(null);
    return this.fetchImpl(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (id !== self.requestId) return null;
        if (!json || !json.success) {
          var invalid = !!json && json.error === 'invalid_oe_size';
          self._fail(i18n(self.finder, invalid ? 'oe-invalid' : 'error'), invalid ? 'invalid' : 'error');
          return null;
        }
        return json;
      })
      .catch(function () {
        if (id === self.requestId) self._fail(i18n(self.finder, 'error'), 'error');
        return null;
      });
  };

  PackageController.prototype.retry = function () {
    return this._retry ? this._retry() : null;
  };

  PackageController.prototype.chooseMode = function (mode) {
    var tabs = this.doc.querySelectorAll('[data-pro-mode-tab]');
    for (var i = 0; i < tabs.length; i++) {
      var on = tabs[i].getAttribute('data-pro-mode-tab') === mode;
      tabs[i].setAttribute('aria-selected', String(on));
      // Roving tabindex: the selected tab is the one tab stop, the arrow keys reach the other.
      tabs[i].setAttribute('tabindex', on ? '0' : '-1');
    }
    var panels = this.doc.querySelectorAll('[data-pro-mode-panel]');
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-pro-mode-panel') !== mode;
    }
    // The results bar is shared: on the Wheels tab the CSS keeps only its
    // quantity stepper (the season chips and Front / rear are tire-only).
    var resultsBar = this._q('.tt-pro-results-bar');
    if (resultsBar) resultsBar.setAttribute('data-pro-tab', mode);
    this.mode = mode;
    if (mode === 'wheels') this._loadYears();
  };

  /** A saved tire list is only that: the chosen size, and no wheel cards. */
  function savedTireList(saved, size) {
    if (!saved || !saved.query || !saved.quotes || !saved.quotes.length) return false;
    if (size && saved.query !== size) return false;
    for (var i = 0; i < saved.quotes.length; i++) {
      var fitment = saved.quotes[i] && saved.quotes[i].fitment;
      if (fitment && fitment.kind === 'rim') return false;
    }
    return true;
  }

  /** Tires tab: the panel, and the tires for the fitment chosen now.
   * A list saved for an older size, or a wheel list, is not put back. */
  PackageController.prototype.showTires = function () {
    this.chooseMode('tires');
    var search = this.search;
    if (!search) return null;
    var state = this._sessionState();
    var size = state && state.chosen && state.chosen.tireSize;
    var saved = search._tireList;
    if (size && !savedTireList(saved, size) && typeof search.searchVehicle === 'function') {
      return search.searchVehicle(state.chosen);
    }
    if (savedTireList(saved) && typeof search._render === 'function') {
      search._listKind = 'tires';
      search.lastQuery = saved.query;
      search._render(saved.quotes, saved.query, search.requestId, saved.offline || null);
      return null;
    }
    var typed = search.input && String(search.input.value || '').trim();
    if (typed && typeof search._search === 'function') return search._search(typed);
    return null;
  };

  /** Specs | Vehicle on the Wheels tab. Like the Tires switch it only shows
   * and hides: no value is cleared and no search starts. */
  PackageController.prototype.chooseWheelMode = function (mode) {
    var buttons = this.doc.querySelectorAll('[data-pro-wheel-mode]');
    var at = 0;
    for (var i = 0; i < buttons.length; i++) {
      var on = buttons[i].getAttribute('data-pro-wheel-mode') === mode;
      buttons[i].setAttribute('aria-pressed', String(on));
      if (on) at = i;
    }
    var group = this._q('[data-pro-wheel-switch]');
    var indicator = group && group.querySelector('.tt-pro-seg__ind');
    if (indicator && indicator.style) indicator.style.transform = 'translateX(' + at * 100 + '%)';
    var panels = this.doc.querySelectorAll('[data-pro-wheel-panel]');
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-pro-wheel-panel') !== mode;
    }
  };

  PackageController.prototype._loadDiameters = function () {
    var self = this;
    if (this._diameterOff) {
      this._diameterOff = false;
      this._show('[data-pro-diameter-group]', true);
    }
    this._loadingChips('[data-pro-diameter-chips]');
    return this._getJson(fitmentRimsUrl(this.base, { kind: this.kind }), this._loadDiameters.bind(this)).then(function (json) {
      if (!json) return;
      var options = (json.diameterOptions || []).map(function (o) { return { value: o.diameterIn }; });
      self._chips('[data-pro-diameter-chips]', 'data-pro-wheel-diameter', options, function (o) { return o.value + '"'; });
      self._status(options.length ? '' : i18n(self.finder, 'no-wheels'));
    });
  };

  PackageController.prototype._loadPcds = function (params) {
    var self = this;
    var query = { kind: this.kind };
    Object.keys(params).forEach(function (k) { query[k] = params[k]; });
    this._show('[data-pro-pcd-group]', true);
    this._loadingChips('[data-pro-pcd-chips]');
    return this._getJson(fitmentRimsUrl(this.base, query), this._loadPcds.bind(this, params)).then(function (json) {
      if (!json) return;
      var options = (json.pcdOptions || []).map(function (o) { return { value: o.pcd, count: o.count }; });
      self._chips('[data-pro-pcd-chips]', 'data-pro-wheel-pcd', options, function (o) { return formatPcd(o.value) + ' · ' + o.count; });
      self._show('[data-pro-pcd-group]', options.length > 0);
      self._status(options.length ? '' : i18n(self.finder, 'no-wheels'));
    });
  };

  PackageController.prototype.openWheels = function () {
    this.chooseMode('wheels');
    this.forTire = null;
    this.diameter = null;
    this._setContext('');
    this._show('[data-pro-diameter-group]', true);
    this._show('[data-pro-pcd-group]', false);
    this._show('[data-pro-tire-finder]', false);
    return this._loadDiameters();
  };

  /** Wheels tab, when a car is already chosen: Vehicle, not Specs, and the
   * list below becomes the wheels for that car. */
  PackageController.prototype.openWheelsForVehicle = function () {
    var state = this._sessionState();
    var chosen = state && state.chosen;
    if (!chosen) return this.openWheels();
    var packaged = this.pkg && this.pkg.tire ? tireSizeOf(this.pkg.tire) : null;
    var fromChosen = chosen.tireSize ? parseTireSize(chosen.tireSize) : null;
    var tire = fromChosen || packaged;
    this.chooseMode('wheels');
    this.chooseWheelMode('vehicle');
    this._show('[data-pro-diameter-group]', false);
    this._show('[data-pro-pcd-group]', false);
    this._show('[data-pro-tire-finder]', false);
    this._pathButtons();
    this._markWheelPath('vehicle');
    if (tire) {
      this.forTire = tire.size;
      this.tireDiameter = tire.rimIn;
      this._setContext(i18n(this.finder, 'wheels-for'), '{size}', tire.size, !fromChosen && !!packaged);
    }
    return this._quoteKnownVehicleWheels();
  };

  /** "Remove tire ›" (locale key package.clear) beside "Wheels that fit
   * 225/55R18": the context comes from the tire in the package, so clearing it
   * takes the tire out (the same call as the tray's remove button) and goes
   * back to a plain wheel search. */
  PackageController.prototype.clearContext = function () {
    this.wheelPath = null;
    this.tireDiameter = null;
    this._show('[data-pro-wheel-paths]', false);
    if (this.pkg && this.pkg.tire) this.removeFromPackage('tire');
    return this.openWheels();
  };

  /** The tray's "Show 18" wheels ›": the Wheels tab, Specs, that diameter. */
  PackageController.prototype.showDiameter = function (diameter) {
    var self = this;
    this.chooseMode('wheels');
    this.chooseWheelMode('specs');
    this._show('[data-pro-diameter-group]', true);
    this._show('[data-pro-tire-finder]', false);
    // The link sits in the tray, a band at the bottom of a phone, while the
    // finder it opens is above the fold: bring it to the dealer.
    var phone = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    if (phone && this.finder && typeof this.finder.scrollIntoView === 'function') this.finder.scrollIntoView({ block: 'start' });
    return this._loadDiameters().then(function () {
      return self.chooseDiameter(diameter);
    });
  };

  PackageController.prototype.chooseKind = function (kind) {
    this.kind = kind;
    this._press('data-pro-wheel-kind', kind);
    if (this.forTire) return this._loadPcds({ tire: this.forTire });
    this.diameter = null;
    this._show('[data-pro-pcd-group]', false);
    return this._loadDiameters();
  };

  PackageController.prototype.chooseDiameter = function (diameter) {
    this.diameter = diameter;
    this._press('data-pro-wheel-diameter', diameter);
    return this._loadPcds({ diameter: diameter });
  };

  PackageController.prototype.choosePcd = function (pcd) {
    this._armList();
    var self = this;
    this._press('data-pro-wheel-pcd', pcd);
    var params = this.forTire
      ? { tire: this.forTire, pcd: pcd, kind: this.kind }
      : { diameter: this.diameter, pcd: pcd, kind: this.kind };
    var label = (this.forTire || this.diameter + '"') + ' · ' + formatPcd(pcd);
    return this._getJson(fitmentRimsUrl(this.base, params), this.choosePcd.bind(this, pcd)).then(function (json) {
      if (!json || !self.search) return;
      if (self.wheelPath === 'specs') return self._quoteFiltered(json, 'specs', label);
      var f = fitmentFromRims(json);
      return self.search.quoteVariants(f.ids, label, f.byId);
    });
  };

  /** assets/toee-pro.js, which loads first on the page. An older cached copy of it may lack
   * what a newer one has, so callers check for the function they want. */
  function loadedProApi() {
    if (typeof ToeePro !== 'undefined') return ToeePro;
    if (typeof require === 'function') {
      try { return require('./toee-pro.js'); } catch (e) { return null; }
    }
    return null;
  }

  /** The hard block on short stock (owner ruling 2026-10-22), from assets/toee-pro.js:
   * { count, quantity, title } when a complete package's stock cannot fill it, else null. */
  function packageShortfall(pkg) {
    var pro = loadedProApi();
    if (!pkg || !pkg.tire || !pkg.rim || !pro || !pro.quoteShortfall) return null;
    return pro.quoteShortfall(buildPackageQuote(pkg));
  }

  /** A complete package that fits and that the stock can fill: the one kind that may open a quote. */
  function packageQuotable(pkg) {
    return packageReady(pkg) && !packageShortfall(pkg);
  }

  function loadedVehicleApi() {
    if (typeof ToeeProVehicle !== 'undefined') return ToeeProVehicle;
    if (typeof require === 'function') {
      try { return require('./toee-pro-vehicle.js'); } catch (e) { return null; }
    }
    return null;
  }

  /** The vehicle the Tires lookup chose. With `withChip` (only "Match the vehicle"),
   * a Wheels tab vehicle counts when the Tires lookup has none: the fitment chip
   * that is pressed names the wheel the vehicle takes. */
  PackageController.prototype._vehicleChoice = function (withChip) {
    var controller = this.vehicle ? this.vehicle() : null;
    var chosen = controller && controller.state && controller.state.chosen;
    if (!chosen || !chosen.boltPattern) {
      var chip = withChip ? this._pressedChip : null;
      return chip ? { boltPattern: chip.pcd, hubBoreMm: chip.hubBoreMm, front: { rimDiameterIn: chip.diameterIn } } : null;
    }
    var fitment = ((controller.state && controller.state.fitments) || []).filter(function (row) {
      return row.boltPattern === chosen.boltPattern && row.front && row.front.tireSize === chosen.tireSize;
    })[0];
    return {
      boltPattern: chosen.boltPattern,
      hubBoreMm: chosen.hubBoreMm,
      front: { rimDiameterIn: fitment && fitment.front ? fitment.front.rimDiameterIn : null }
    };
  };

  /** Specs | Vehicle is the switch. The old Match / Choose buttons are not shown. */
  PackageController.prototype._pathButtons = function () {
    var box = this._q('[data-pro-wheel-paths]');
    if (box) box.hidden = true;
  };

  /** A click on Specs or Vehicle. Specs opens the diameter list. Vehicle uses the car already chosen. */
  PackageController.prototype.pickWheelMode = function (mode) {
    this.chooseWheelMode(mode);
    if (mode === 'specs') {
      this.wheelPath = 'specs';
      this._show('[data-pro-diameter-group]', true);
      this._show('[data-pro-pcd-group]', false);
      var controller = this.vehicle ? this.vehicle() : null;
      if (controller && controller.wheelSpecChanged) controller.wheelSpecChanged();
      return this._loadDiameters();
    }
    var state = this._sessionState();
    var chosen = state && state.chosen;
    if (!chosen) return Promise.resolve();
    return this._quoteKnownVehicleWheels();
  };

  /** `fitment` (the Wheels Vehicle lookup's chip) replaces the Tires vehicle
   * and the tire's diameter: the chip names the wheel itself. */
  PackageController.prototype._quoteFiltered = function (json, mode, label, fitment) {
    var api = loadedVehicleApi();
    var vehicleChoice = fitment || this._vehicleChoice(mode === 'vehicle') || {};
    var wheels = ((json && json.matches) || []).map(function (match) {
      var spec = match.spec || {};
      var pcds = spec.pcds || [];
      var bolt = vehicleChoice.boltPattern && pcds.indexOf(vehicleChoice.boltPattern) >= 0
        ? vehicleChoice.boltPattern
        : (pcds[0] || '');
      return { id: String(match.variantId), diameterIn: spec.diameterIn, boltPattern: bolt, cbMm: spec.cbMm, spec: spec };
    });
    var selected = api && api.selectPackageWheels
      ? api.selectPackageWheels({ mode: mode, tireDiameter: fitment ? null : this.tireDiameter, fitment: vehicleChoice, wheels: wheels })
      : [];
    var ids = [];
    var byId = {};
    selected.forEach(function (row) {
      var wheel = row.wheel;
      ids.push(wheel.id);
      var fitment = { kind: 'rim', spec: wheel.spec };
      if (row.hubRing) {
        fitment.hubRing = {
          outerMm: wheel.cbMm,
          innerMm: vehicleChoice.hubBoreMm,
          href: '/products/car-wheel-hub-centric-rings?od=' + encodeURIComponent(wheel.cbMm) + '&id=' + encodeURIComponent(vehicleChoice.hubBoreMm)
        };
      }
      if (row.hints && row.hints.length) fitment.hints = row.hints;
      if (row.oeDiameterHint != null) fitment.oeDiameterHint = row.oeDiameterHint;
      byId[wheel.id] = fitment;
    });
    if (!this.search) return Promise.resolve();
    return this.search.quoteVariants(ids, label, byId);
  };

  PackageController.prototype.findWheelsForTire = function (tireQuote) {
    var tire = parseTireSize((tireQuote.fitment && tireQuote.fitment.size) || tireQuote.title);
    if (!tire) return Promise.resolve();
    this.chooseMode('wheels');
    this.forTire = tire.size;
    this.tireDiameter = tire.rimIn;
    this._setContext(i18n(this.finder, 'wheels-for'), '{size}', tire.size, true);
    this._show('[data-pro-diameter-group]', false);
    this._show('[data-pro-tire-finder]', false);
    this._pathButtons();
    var state = this._sessionState();
    var chosen = state && state.chosen;
    if (chosen && chosen.boltPattern) {
      this.chooseWheelMode('vehicle');
      this._markWheelPath('vehicle');
      return this._quoteKnownVehicleWheels();
    }
    return Promise.resolve();
  };

  /** The car the tire lookup already holds, if it names a bolt pattern or a year. */
  PackageController.prototype._sessionState = function () {
    var controller = this.vehicle ? this.vehicle() : null;
    return controller && controller.state ? controller.state : null;
  };

  PackageController.prototype._markWheelPath = function (mode) {
    var buttons = this.doc.querySelectorAll('[data-pro-wheel-path]');
    for (var i = 0; i < buttons.length; i++) {
      var on = buttons[i].getAttribute('data-pro-wheel-path') === mode;
      buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  };

  /** Put the car we already know on the form, without waiting for another lookup.
   * The option lists fill in behind it. The selected row is what the dealer sees. */
  PackageController.prototype._selectKnown = function (field, value, label) {
    var el = this._chain(field);
    if (!el) return;
    if (value == null || value === '') {
      this._fillChain(field, [], '');
      return;
    }
    var children = el.children || [];
    var have = false;
    for (var i = 0; i < children.length; i++) {
      if (String(children[i].value) === String(value)) have = true;
    }
    if (!have) this._fillChain(field, [], value, label || value);
    el.value = String(value);
    el.disabled = false;
  };

  PackageController.prototype._showKnownVehicle = function () {
    var state = this._sessionState();
    var chosen = state && state.chosen;
    if (!chosen || !chosen.year || !this._chain('year')) return false;
    var card = state.card || {};
    this._selectKnown('year', chosen.year, state.yearName || chosen.year);
    this._selectKnown('make', chosen.make, card.makeName || state.makeName || chosen.make);
    this._selectKnown('model', chosen.model, card.modelName || state.modelName || chosen.model);
    this._selectKnown('trim', chosen.trim || '', state.trimName || chosen.trim || '');
    this._gateChain(this._chainValues());
    if (state.fitments && state.fitments.length) this._paintKnownFitments(state);
    return true;
  };

  /** The wheel list uses the tire size and the car already chosen. It does not
   * wait for Year / Make / Model / Trim to be fetched again. */
  PackageController.prototype._quoteKnownVehicleWheels = function () {
    this._showKnownVehicle();
    if (!this.forTire) {
      var state = this._sessionState();
      var chosen = state && state.chosen;
      var fromPackage = this.pkg && this.pkg.tire ? tireSizeOf(this.pkg.tire) : null;
      var tire = fromPackage || (chosen && chosen.tireSize ? parseTireSize(chosen.tireSize) : null);
      if (tire) {
        this.forTire = tire.size;
        this.tireDiameter = tire.rimIn;
      }
    }
    var self = this;
    var seen = this._fitmentChoice || 0;
    var now = this._sessionState();
    var chosenNow = now && now.chosen;
    var wheels = seen === this._fitmentChoice && this.forTire && chosenNow && chosenNow.boltPattern
      ? this.chooseWheelPath('vehicle')
      : Promise.resolve(null);
    var lists = this.applyKnownVehicle();
    return Promise.all([wheels, lists]).then(function () { return wheels; });
  };

  /** Year / Make / Model / Trim, and the fitment chips, from the car already chosen. */
  PackageController.prototype.applyKnownVehicle = function () {
    var state = this._sessionState();
    var chosen = state && state.chosen;
    if (!chosen || !chosen.year || !chosen.make || !chosen.model || !this.wheelVehicle || !this._chain('year')) {
      return Promise.resolve(false);
    }
    var self = this;
    var seen = this._fitmentChoice || 0;
    var card = state.card || {};
    var names = {
      year: state.yearName || chosen.year,
      make: card.makeName || state.makeName || chosen.make,
      model: card.modelName || state.modelName || chosen.model,
      trim: state.trimName || chosen.trim || ''
    };
    function load(params) {
      return Promise.resolve(self.wheelVehicle.loadOptions(params));
    }
    return load({}).then(function (years) {
      if (seen !== self._fitmentChoice || !years) return false;
      self._fillChain('year', years, chosen.year, names.year);
      return load({ year: chosen.year });
    }).then(function (makes) {
      if (makes === false || seen !== self._fitmentChoice) return false;
      self._fillChain('make', makes, chosen.make, names.make);
      return load({ year: chosen.year, make: chosen.make });
    }).then(function (models) {
      if (models === false || seen !== self._fitmentChoice) return false;
      self._fillChain('model', models, chosen.model, names.model);
      return load({ year: chosen.year, make: chosen.make, model: chosen.model });
    }).then(function (trims) {
      if (trims === false || seen !== self._fitmentChoice) return false;
      if (chosen.trim) self._fillChain('trim', trims, chosen.trim, names.trim);
      else self._fillChain('trim', trims, '');
      self._gateChain(self._chainValues());
      self._paintKnownFitments(self._sessionState() || state);
      return true;
    }, function () {
      return false;
    });
  };

  PackageController.prototype._paintKnownFitments = function (state) {
    var chips = vehicleWheelChips(state && state.fitments);
    this._vehicleChips = chips;
    this._chips('[data-pro-fitment-chips]', 'data-pro-wheel-fitment', chips.map(function (c, i) {
      return { value: i, label: c.label };
    }), function (o) { return o.label; });
    this._show('[data-pro-fitment-group]', chips.length > 0);
    this._show('[data-pro-fitment-none]', !chips.length && !!(state && state.fitments && state.fitments.length));
    var chosen = state && state.chosen;
    var picked = state && state.fitments && state.chosenIndex != null ? state.fitments[state.chosenIndex] : null;
    var diameter = picked && picked.front ? picked.front.rimDiameterIn : null;
    var match = -1;
    for (var i = 0; i < chips.length; i++) {
      if (chosen && chips[i].pcd === chosen.boltPattern && (diameter == null || chips[i].diameterIn === diameter)) {
        match = i;
        break;
      }
    }
    if (match >= 0) {
      this._press('data-pro-wheel-fitment', match);
      this._pressedChip = chips[match];
    }
  };

  PackageController.prototype.useChosenVehicle = function () {
    this.wheelPath = 'vehicle';
    return this.chooseWheelPath('vehicle');
  };

  PackageController.prototype.chooseWheelPath = function (mode) {
    this._armList();
    var tire = this.forTire ? parseTireSize(this.forTire) : tireSizeOf(this.pkg && this.pkg.tire);
    if (!tire) return Promise.resolve();
    this.forTire = tire.size;
    this.tireDiameter = tire.rimIn;
    this.wheelPath = mode;
    this._markWheelPath(mode);
    if (mode === 'vehicle') {
      var vehicleChoice = this._vehicleChoice(true);
      if (!vehicleChoice) {
        // No vehicle yet, on either tab: show where to pick one.
        this.chooseWheelMode('vehicle');
        return Promise.resolve();
      }
      this._show('[data-pro-diameter-group]', false);
      this._show('[data-pro-pcd-group]', false);
      var self = this;
      return this._getJson(fitmentRimsUrl(this.base, { tire: tire.size, pcd: vehicleChoice.boltPattern, kind: this.kind }), this.chooseWheelPath.bind(this, mode))
        .then(function (json) {
          if (!json) return;
          return self._quoteFiltered(json, 'vehicle', tire.size + ' · ' + formatPcd(vehicleChoice.boltPattern));
        });
    }
    var controller = this.vehicle ? this.vehicle() : null;
    if (controller && controller.wheelSpecChanged) controller.wheelSpecChanged();
    // The diameter group lives in the Specs panel: with the switch on Vehicle,
    // loading it would change nothing the dealer can see.
    this.chooseWheelMode('specs');
    this._show('[data-pro-diameter-group]', true);
    this._show('[data-pro-pcd-group]', false);
    return this._loadDiameters();
  };

  /** The tire size this car uses on a rim of this diameter. Find tires uses it
   * as the OE size so the list is tires for the car, not every tire that mounts on the rim. */
  PackageController.prototype._oeSizeForRim = function (spec) {
    var state = this._sessionState();
    if (!state || !spec || spec.diameterIn == null) return null;
    var diameter = Number(spec.diameterIn);
    var chosenSize = state.chosen && state.chosen.tireSize;
    var fallback = null;
    var list = state.fitments || [];
    for (var i = 0; i < list.length; i++) {
      var row = list[i] || {};
      var axles = [row.front, row.rear];
      for (var a = 0; a < axles.length; a++) {
        var axle = axles[a];
        if (!axle || !axle.tireSize || Number(axle.rimDiameterIn) !== diameter) continue;
        if (chosenSize && axle.tireSize === chosenSize) return axle.tireSize;
        if (!fallback) fallback = axle.tireSize;
      }
    }
    if (chosenSize) {
      var parsed = parseTireSize(chosenSize);
      if (parsed && parsed.rimIn === diameter) return chosenSize;
    }
    return fallback;
  };

  PackageController.prototype.showTireFinder = function (rimQuote) {
    var spec = rimQuote.fitment && rimQuote.fitment.spec;
    if (!spec) return Promise.resolve();
    this.chooseMode('wheels');
    this._setContext(i18n(this.finder, 'tires-for'), '{wheel}', spec.diameterIn + '×' + formatInches(spec.widthIn), false);
    this._show('[data-pro-diameter-group]', false);
    this._show('[data-pro-pcd-group]', false);
    this._show('[data-pro-tire-finder]', true);
    var input = this._q('[data-pro-oe-input]');
    var oe = this._oeSizeForRim(spec);
    if (input && oe && !String(input.value || '').trim()) input.value = oe;
    return Promise.resolve();
  };

  PackageController.prototype.findTires = function () {
    this._armList();
    var self = this;
    var spec = this.pkg.rim && this.pkg.rim.fitment && this.pkg.rim.fitment.spec;
    if (!spec) return Promise.resolve();
    var input = this._q('[data-pro-oe-input]');
    var raw = input ? String(input.value || '').trim() : '';
    if (!raw) {
      raw = this._oeSizeForRim(spec) || '';
      if (raw && input) input.value = raw;
    }
    var oe = raw ? parseTireSize(raw) : null;
    if (raw && !oe) {
      this._status(i18n(this.finder, 'oe-invalid'), 'invalid');
      return Promise.resolve();
    }
    var winter = this._q('[data-pro-winter-only]');
    var wheel = spec.diameterIn + '×' + formatInches(spec.widthIn);
    var params = {
      rimDiameter: spec.diameterIn,
      rimWidth: spec.widthIn,
      oe: oe ? oe.size : '',
      season: winter && winter.checked ? 'WINTER' : ''
    };
    return this._getJson(fitmentTiresUrl(this.base, params), this.findTires.bind(this)).then(function (json) {
      if (!json || !self.search) return;
      var f = fitmentFromTires(json);
      return self.search.quoteVariants(f.ids, oe ? oe.size + ' → ' + wheel : wheel, f.byId);
    });
  };

  // ---------------------------------------------------------------------
  // Find wheels by Vehicle (rollout Task 7, Wheels-states A). The Tires
  // tab's Year → Make → Model → Trim lookup: the same controller
  // (ToeePro.createVehicleController, its own instance), the same gating
  // (ToeeProFind.vehicleChainState / clearAfter). A fitment chip runs the
  // very request a Specs search runs for its diameter and bolt pattern, and
  // keeps the wheels whose centre bore goes on the hub (selectPackageWheels,
  // as "Match the vehicle" does).
  // ---------------------------------------------------------------------

  var CHAIN = ['year', 'make', 'model', 'trim'];

  function loadedFindApi() {
    if (typeof ToeeProFind !== 'undefined') return ToeeProFind;
    if (typeof require === 'function') {
      try { return require('./toee-pro-find.js'); } catch (e) { return null; }
    }
    return null;
  }

  /** One chip per wheel the vehicle takes (front, and a staggered rear):
   * `17" · 5×114.3 · CB 64.1 · ET 50`. Two tire sizes on one wheel are one
   * chip; a fitment without a bolt pattern has no wheel search, so no chip. */
  function vehicleWheelChips(fitments) {
    var out = [];
    var seen = {};
    (fitments || []).forEach(function (f) {
      if (!f || !f.boltPattern) return;
      var rearHub = f.rearHubBoreMm != null ? f.rearHubBoreMm : f.hubBoreMm;
      [[f.front, f.hubBoreMm], [f.rear, rearHub]].forEach(function (pair) {
        var axle = pair[0];
        var hub = pair[1] == null ? null : pair[1];
        if (!axle || !axle.rimDiameterIn) return;
        var parts = [axle.rimDiameterIn + '"', formatPcd(f.boltPattern)];
        if (hub != null) parts.push('CB ' + hub);
        if (axle.offsetMm != null) parts.push('ET ' + axle.offsetMm);
        var label = parts.join(' · ');
        if (seen[label]) return;
        seen[label] = true;
        out.push({ label: label, diameterIn: axle.rimDiameterIn, pcd: f.boltPattern, hubBoreMm: hub });
      });
    });
    return out;
  }

  /** The session fitment a wheel chip names. Chip order is not fitment order:
   * one chip can stand for a front wheel, and a staggered rear is its own. */
  function fitmentIndexForChip(state, chip) {
    var list = (state && state.fitments) || [];
    var i;
    var rearAt = -1;
    for (i = 0; i < list.length; i++) {
      var row = list[i];
      if (!row || row.boltPattern !== chip.pcd) continue;
      var front = row.front;
      if (front && Number(front.rimDiameterIn) === Number(chip.diameterIn)) {
        if (chip.hubBoreMm == null || row.hubBoreMm == null || Number(row.hubBoreMm) === Number(chip.hubBoreMm)) return i;
      }
      var rear = row.rear;
      var rearHub = row.rearHubBoreMm != null ? row.rearHubBoreMm : row.hubBoreMm;
      if (rearAt < 0 && rear && Number(rear.rimDiameterIn) === Number(chip.diameterIn)) {
        if (chip.hubBoreMm == null || rearHub == null || Number(rearHub) === Number(chip.hubBoreMm)) rearAt = i;
      }
    }
    return rearAt;
  }

  PackageController.prototype._chain = function (field) {
    return this._q('[data-pro-wheel-vehicle-field="' + field + '"]');
  };

  PackageController.prototype._chainValues = function () {
    var out = {};
    for (var i = 0; i < CHAIN.length; i++) {
      var el = this._chain(CHAIN[i]);
      out[CHAIN[i]] = el ? String(el.value || '') : '';
    }
    return out;
  };

  /** A list opens once the one before it is chosen; Find fitments needs
   * year, make and model (trim stays optional). */
  PackageController.prototype._gateChain = function (values) {
    var find = loadedFindApi();
    var state = find ? find.vehicleChainState(values) : {};
    var self = this;
    ['make', 'model', 'trim'].forEach(function (f) {
      var el = self._chain(f);
      if (el) el.disabled = !!state[f + 'Off'];
    });
    var button = this._q('[data-pro-wheel-find-fitments]');
    if (button) button.disabled = !!state.findOff;
  };

  /** The list's first option is its own label (Liquid); the rest are replaced. */
  PackageController.prototype._fillChain = function (field, options, selected, selectedLabel) {
    var el = this._chain(field);
    if (!el) return;
    var nodes = el.children && el.children[0] ? [el.children[0]] : [];
    var self = this;
    (options || []).forEach(function (o) {
      var option = self._make('option', '', String(o.label));
      option.value = String(o.value);
      nodes.push(option);
    });
    if (selected) {
      var have = false;
      for (var n = 0; n < nodes.length; n++) {
        if (String(nodes[n].value) === String(selected)) have = true;
      }
      if (!have) {
        var extra = self._make('option', '', String(selectedLabel || selected));
        extra.value = String(selected);
        nodes.push(extra);
      }
    }
    el.replaceChildren.apply(el, nodes);
    el.value = selected ? String(selected) : '';
    if (selected) el.disabled = false;
  };

  /** The year list, the first time the Wheels tab opens (again after a failure). */
  PackageController.prototype._loadYears = function () {
    if (this._years || !this.wheelVehicle || !this._chain('year')) return;
    var self = this;
    this._years = Promise.resolve(this.wheelVehicle.loadOptions({})).then(function (options) {
      var year = self._chain('year');
      self._fillChain('year', options, year && year.value);
    }, function () {
      self._years = null;
    });
  };

  /** The chips belong to the vehicle they were found for, so a changed list or
   * a new Find takes them away: the chips, the "no fitment" hint, a Find still
   * loading (its answer is dropped by `_fitmentAsk`) and a chip's wheel search
   * still in flight (dropped by `requestId`: it would list wheels for a car that
   * is no longer chosen). Only a chip's own search is cancelled; bumping the id
   * under a Specs request would leave its skeleton chips standing. */
  /** A new list the dealer just asked for. One that was already in flight stays stale. */
  PackageController.prototype._armList = function () {
    this._activeListGeneration = this._listGeneration || 0;
  };

  /** Clear vehicle: the wheel form, its chips, the package and the finder go with the car. */
  PackageController.prototype.clearVehicleContext = function () {
    this._dropFitments();
    this._listGeneration = (this._listGeneration || 0) + 1;
    this.requestId += 1;
    var fields = ['year', 'make', 'model', 'trim'];
    for (var i = 0; i < fields.length; i++) {
      var el = this._chain(fields[i]);
      if (!el) continue;
      el.value = '';
      if (typeof el.selectedIndex === 'number') el.selectedIndex = 0;
      if (fields[i] !== 'year') el.disabled = true;
    }
    var find = this._q('[data-pro-wheel-find-fitments]');
    if (find) find.disabled = true;
    this.forTire = null;
    this.tireDiameter = null;
    this.diameter = null;
    this.wheelPath = null;
    this.kind = 'all';
    this._setContext('');
    this._show('[data-pro-diameter-group]', false);
    this._show('[data-pro-pcd-group]', false);
    this._show('[data-pro-tire-finder]', false);
    ['[data-pro-diameter-chips]', '[data-pro-pcd-chips]'].forEach(function (sel) {
      var box = this._q(sel);
      while (box && box.firstChild) box.removeChild(box.firstChild);
    }, this);
    var oe = this._q('[data-pro-oe-input]');
    if (oe) oe.value = '';
    var winter = this._q('[data-pro-winter-only]');
    if (winter) winter.checked = false;
    this.pkg = emptyPackage();
    this.renderBar();
  };

  PackageController.prototype._dropFitments = function () {
    this._fitmentAsk += 1;
    this._fitmentChoice = (this._fitmentChoice || 0) + 1;
    if (this._chipAsk === this.requestId) this.requestId += 1;
    // A chip's failed search leaves an error whose Try again names a chip that is gone.
    if (this._retry && this._retry === this._chipRetry) {
      this._retry = null;
      this._status('');
    }
    this._chipAsk = null;
    this._chipRetry = null;
    this._pressedChip = null;
    this._vehicleChips = [];
    var box = this._q('[data-pro-fitment-chips]');
    while (box && box.firstChild) box.removeChild(box.firstChild);
    this._show('[data-pro-fitment-group]', false);
    this._show('[data-pro-fitment-none]', false);
  };

  PackageController.prototype.chooseVehicleField = function (field) {
    var find = loadedFindApi();
    var values = find ? find.clearAfter(this._chainValues(), field) : this._chainValues();
    var at = CHAIN.indexOf(field);
    var self = this;
    CHAIN.slice(at + 1).forEach(function (f) { self._fillChain(f, []); });
    this._gateChain(values);
    this._dropFitments();
    // Every change is a new question, even one that asks nothing (a list cleared
    // while the next one loads): an older list answer is dropped.
    var ask = ++this._chainAsk;
    var next = CHAIN[at + 1];
    if (!next || !values[field] || !this.wheelVehicle) return Promise.resolve();
    var params = {};
    CHAIN.slice(0, at + 1).forEach(function (f) { params[f] = values[f]; });
    return Promise.resolve(this.wheelVehicle.loadOptions(params)).then(function (options) {
      if (ask !== self._chainAsk) return;
      self._fillChain(next, options);
    }, function () {});
  };

  PackageController.prototype.findVehicleFitments = function () {
    var v = this._chainValues();
    if (!v.year || !v.make || !v.model || !this.wheelVehicle) return Promise.resolve();
    var self = this;
    this._dropFitments();
    var ask = this._fitmentAsk;
    this._retry = this.findVehicleFitments.bind(this);
    this._status('');
    this._show('[data-pro-fitment-group]', true);
    this._loadingChips('[data-pro-fitment-chips]');
    return Promise.resolve(this.wheelVehicle.findFitments({ year: v.year, make: v.make, model: v.model, trim: v.trim || undefined }))
      .then(function (state) {
        if (ask !== self._fitmentAsk) return;
        var chips = vehicleWheelChips(state && state.fitments);
        self._vehicleChips = chips;
        self._chips('[data-pro-fitment-chips]', 'data-pro-wheel-fitment', chips.map(function (c, i) {
          return { value: i, label: c.label };
        }), function (o) { return o.label; });
        self._show('[data-pro-fitment-group]', chips.length > 0);
        self._show('[data-pro-fitment-none]', !chips.length);
      }, function () {
        if (ask !== self._fitmentAsk) return;
        self._fail(i18n(self.finder, 'error'), 'error');
      });
  };

  /** The wheel chip is the latest fitment. It is written into the one vehicle
   * every tab reads, and a refresh that started earlier must not paint the old one. */
  PackageController.prototype._rememberFitment = function (chip) {
    var vehicle = this.vehicle ? this.vehicle() : null;
    if (!vehicle || typeof vehicle.pickFitment !== 'function' || !chip) return;
    var index = fitmentIndexForChip(vehicle.state, chip);
    if (index < 0 || (vehicle.state && vehicle.state.chosenIndex === index)) return;
    var row = vehicle.state.fitments[index];
    var axle = row && row.front && Number(row.front.rimDiameterIn) === Number(chip.diameterIn)
      ? row.front
      : (row && row.rear && Number(row.rear.rimDiameterIn) === Number(chip.diameterIn) ? row.rear : (row && row.front));
    if (axle && axle.tireSize) {
      this.forTire = axle.tireSize;
      var parsed = parseTireSize(axle.tireSize);
      if (parsed) this.tireDiameter = parsed.rimIn;
      this._setContext(i18n(this.finder, 'wheels-for'), '{size}', axle.tireSize, false);
    }
    vehicle.pickFitment(index, { source: 'wheels' });
  };

  PackageController.prototype.chooseVehicleFitment = function (index) {
    var chip = (this._vehicleChips || [])[index];
    if (!chip) return Promise.resolve();
    this._armList();
    this._fitmentChoice = (this._fitmentChoice || 0) + 1;
    var self = this;
    this._press('data-pro-wheel-fitment', index);
    this._pressedChip = chip;
    this._rememberFitment(chip);
    // All wheel types: the Wheel type filter is a Specs control, invisible on this panel.
    var url = fitmentRimsUrl(this.base, { diameter: chip.diameterIn, pcd: chip.pcd, kind: 'all' });
    var retry = this.chooseVehicleFitment.bind(this, index);
    var request = this._getJson(url, retry);
    // _getJson has just taken the next request id: remember it as this chip's.
    this._chipAsk = this.requestId;
    this._chipRetry = retry;
    return request.then(function (json) {
      if (!json) return;
      self._chipRetry = null; // it worked: nothing left to try again
      return self._quoteFiltered(json, 'vehicle', chip.label, {
        boltPattern: chip.pcd,
        hubBoreMm: chip.hubBoreMm,
        front: { rimDiameterIn: chip.diameterIn }
      });
    });
  };

  /** Redraws whatever result cards are already on screen so their
   * button(s) reflect the new package-mode state - see ProSearch's
   * toee-pro:package-changed listener (assets/toee-pro.js). Guarded the
   * same way ProSettingsPanel.prototype.close already guards its own
   * CustomEvent dispatch elsewhere in this codebase. */
  function announcePackageChanged(doc) {
    if (typeof CustomEvent === 'function' && doc && doc.dispatchEvent) {
      doc.dispatchEvent(new CustomEvent('toee-pro:package-changed'));
    }
  }

  PackageController.prototype.addToPackage = function (side, quote) {
    this.pkg = setPackageSide(this.pkg, side, quote);
    var qty = (quote && quote.requestedQuantity) || 1;
    var other = side === 'tire' ? this.pkg.rim : this.pkg.tire;
    var mismatch = other && (other.requestedQuantity || 1) !== qty;
    var self = this;
    var settled = mismatch ? this.applyQuantity(qty) : Promise.resolve().then(function () {
      self.renderBar();
      if (packageQuotable(self.pkg)) {
        var generateBtn = self._q('[data-pro-package-generate]');
        if (generateBtn && generateBtn.click) generateBtn.click();
      }
      announcePackageChanged(self.doc);
    });
    return Promise.resolve(settled).then(function () {
      if (side === 'tire' && !self.pkg.rim) return self.findWheelsForTire(quote);
      if (side === 'rim' && !self.pkg.tire) return self.showTireFinder(quote);
    });
  };

  /** The stepper is the package quantity: both halves are repriced at it,
   * and a complete package opens a quote for that many. `answered` is a
   * quote-batch answer the results list already asked for with these halves
   * in it (one request per stepper tap); without it, this asks on its own. */
  PackageController.prototype.applyQuantity = function (quantity, answered) {
    var n = Math.floor(Number(quantity));
    if (!n || n < 1) n = 1;
    var pkg = this.pkg || emptyPackage();
    var ids = [];
    if (pkg.tire && pkg.tire.variantId) ids.push(String(pkg.tire.variantId));
    if (pkg.rim && pkg.rim.variantId) ids.push(String(pkg.rim.variantId));
    if (!ids.length) return Promise.resolve();
    var self = this;
    var fetchImpl = this.fetchImpl;
    if (answered === undefined && !fetchImpl) return Promise.resolve();
    // Review I4: an answer for an older quantity must not land on a newer one.
    var ask = this._quantityAsk = (this._quantityAsk || 0) + 1;
    var answer = answered !== undefined ? Promise.resolve(answered) : Promise.resolve(fetchImpl(this.base + '/quote-batch', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ variantIds: ids, quantity: n })
    })).then(function (r) {
      return r && r.json ? r.json() : null;
    });
    return answer.then(function (body) {
      if (ask !== self._quantityAsk) return;
      var byId = {};
      // Only a live answer moves the package: the service worker's cached batch was priced and
      // promised at some other quantity.
      if (body && body.success === true && !body.offline && Array.isArray(body.quotes)) {
        body.quotes.forEach(function (q) {
          if (q && q.variantId != null) byId[String(q.variantId)] = q;
        });
      }
      var sides = ['tire', 'rim'].filter(function (side) { return !!self.pkg[side]; });
      // Review I4: a half with no fresh answer would carry the new quantity on the old price and
      // promise. The package stays as it was, and nothing opens.
      if (!sides.every(function (side) { return !!byId[String(self.pkg[side].variantId)]; })) {
        self.renderBar();
        return;
      }
      sides.forEach(function (side) {
        var cur = self.pkg[side];
        var fresh = byId[String(cur.variantId)];
        // The stock and the delivery promise answer for this quantity too, and say so
        // (promiseQuantity, read by ToeePro.quoteShortfall): keeping the old ones would hide a
        // half that cannot fill it (owner ruling 2026-10-22).
        var next = Object.assign({}, cur, { requestedQuantity: n, promiseQuantity: n, stock: fresh.stock, promise: fresh.promise, errors: fresh.errors });
        if (fresh.price) next.price = fresh.price;
        self.pkg = setPackageSide(self.pkg, side, next);
      });
      self.renderBar();
      if (packageQuotable(self.pkg)) {
        var generateBtn = self._q('[data-pro-package-generate]');
        if (generateBtn && generateBtn.click) generateBtn.click();
      }
      announcePackageChanged(self.doc);
    }).catch(function () {
      if (ask === self._quantityAsk) self.renderBar();
    });
  };

  /** The tab of the mode that is open (the one aria-selected). */
  PackageController.prototype._selectedTab = function () {
    var tabs = this.doc.querySelectorAll('[data-pro-mode-tab]');
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].getAttribute('aria-selected') === 'true') return tabs[i];
    }
    return null;
  };

  PackageController.prototype.removeFromPackage = function (side) {
    var removed = this._q('[data-pro-package-remove="' + side + '"]');
    this.pkg = removePackageSide(this.pkg, side);
    this.renderBar();
    announcePackageChanged(this.doc);
    // The emptied slot's remove button went away with it: on to the other slot's remove
    // button while it has one, else to the tab of the mode that is open.
    var other = this._q('[data-pro-package-remove="' + (side === 'tire' ? 'rim' : 'tire') + '"]');
    var pro = loadedProApi();
    if (pro && pro.handOffFocus) pro.handOffFocus(removed, other && !other.hidden ? other : this._selectedTab());
  };

  PackageController.prototype.renderBar = function () {
    var bar = this.bar;
    if (!bar) return;
    var pkg = this.pkg;
    bar.hidden = !pkg.tire && !pkg.rim;
    var self = this;
    ['tire', 'rim'].forEach(function (side) {
      var q = pkg[side];
      // Remove only on a filled slot (the tray artboards).
      self._show('[data-pro-package-remove="' + side + '"]', !!q);
      var el = self._q('[data-pro-package-name="' + side + '"]');
      if (!el) return;
      el.textContent = q ? q.title : i18n(bar, 'slot-empty') || i18n(bar, side === 'tire' ? 'tire-empty' : 'wheel-empty');
      el.className = 'tt-pro-package__name' + (q ? '' : ' tt-pro-package__name--empty');
    });
    var codes = packageConflicts(pkg);
    var lines = codes.map(function (code) { return i18n(bar, 'conflict-' + code); });
    // A package the stock cannot fill is listed like a clash, naming the half that is short.
    var shortfall = packageShortfall(pkg);
    if (shortfall) lines.push(loadedProApi().shortfallText('screen', i18n(bar, 'short-blocked'), shortfall, i18n(bar, 'short-unproven')));
    var list = this._q('[data-pro-package-conflicts]');
    if (list) {
      list.replaceChildren();
      lines.forEach(function (line) {
        var li = self._make('li');
        li.appendChild(self._make('span', 'tt-pro-marker tt-pro-marker--error')).setAttribute('aria-hidden', 'true');
        li.appendChild(self._make('span', '', line));
        list.appendChild(li);
      });
    }
    this._show('[data-pro-package-callout]', lines.length > 0);
    // The one clash a click can help with: the wheel's size. The tire says
    // which diameter fits, so the link lists those wheels.
    var fix = this._q('[data-pro-package-fix]');
    if (fix) {
      var tire = tireSizeOf(pkg.tire);
      var template = i18n(bar, 'show-diameter');
      var fixable = !!(tire && template && (codes.indexOf('diameter') > -1 || codes.indexOf('width') > -1));
      fix.hidden = !fixable;
      if (fixable) {
        fix.setAttribute('data-pro-package-fix', String(tire.rimIn));
        // The unit lives in the sentence ("{n}\"" / "{n} po"), so only the number goes in.
        fix.textContent = template.replace('{n}', tire.rimIn);
      }
    }
    var guest = !!(pkg.tire && pkg.rim && (!pkg.tire.price || !pkg.rim.price));
    var note = this._q('[data-pro-package-note]');
    if (note) {
      note.textContent = guest ? i18n(bar, 'guest') : '';
      note.hidden = !guest;
    }
    var btn = this._q('[data-pro-package-generate]');
    if (btn) {
      var ready = packageReady(pkg) && !shortfall;
      btn.disabled = !ready;
      // ProQuoteScreen's delegated listener opens whatever this attribute
      // holds; a disabled button never fires, and '' never parses.
      btn.setAttribute('data-pro-generate-quote', ready ? JSON.stringify(buildPackageQuote(pkg)) : '');
    }
    this.measureTray();
  };

  /** On a phone the tray is fixed to the bottom of the screen, so the page keeps
   * room for it: its height goes to --tt-pro-tray-h (read by the section's CSS,
   * which zeroes it from 768px where the tray is inline). Zero while hidden. */
  PackageController.prototype.measureTray = function () {
    var bar = this.bar;
    var root = this.doc && this.doc.documentElement;
    if (!bar || !root || !root.style || !root.style.setProperty) return;
    root.style.setProperty('--tt-pro-tray-h', bar.hidden ? '0px' : (bar.offsetHeight || 0) + 'px');
  };

  /** Fields a keyboard types into: a text-like input or a textarea. */
  function isTextField(node) {
    var tag = node && node.tagName ? String(node.tagName).toLowerCase() : '';
    if (tag === 'textarea') return true;
    if (tag !== 'input') return false;
    var type = String(node.type || (node.getAttribute && node.getAttribute('type')) || 'text').toLowerCase();
    return /^(text|search|tel|url|email|number|password)$/.test(type);
  }

  /** While a text field has focus the tray goes back into the page's flow
   * (the --docked class): a fixed band would float over the iOS keyboard.
   * `target` is the focusin / focusout target; on focusout `next` is where the
   * focus goes, so moving between two fields does not undock and redock it. */
  PackageController.prototype.handleFocus = function (target, entering, next) {
    if (!this.bar) return;
    toggleClass(this.bar, 'tt-pro-package--docked', isTextField(entering ? target : next));
  };

  /** What a tab does when it is chosen, by click or by key. The tab already selected does
   * nothing: openWheels would drop the package's "Wheels that fit …" context and refetch. */
  PackageController.prototype.selectTab = function (tab) {
    if (tab.getAttribute('aria-selected') === 'true') return null;
    if (tab.getAttribute('data-pro-mode-tab') !== 'wheels') return this.showTires();
    var state = this._sessionState();
    var chosen = state && state.chosen;
    if (chosen && chosen.boltPattern) return this.openWheelsForVehicle();
    return this.openWheels();
  };

  /** Arrow keys (wrapping), Home and End on a tab select the tab they reach and focus it, the
   * same call a click makes. Anything else, or a key anywhere but a tab, is left alone:
   * returns true only for a key it handled, so the caller can keep the page from scrolling. */
  PackageController.prototype.handleKey = function (target, key) {
    var tab = target && target.closest ? target.closest('[data-pro-mode-tab]') : null;
    if (!tab) return false;
    var tabs = this.doc.querySelectorAll('[data-pro-mode-tab]');
    var at = Array.prototype.indexOf.call(tabs, tab);
    var last = tabs.length - 1;
    var to = -1;
    if (key === 'ArrowRight') to = at < last ? at + 1 : 0;
    else if (key === 'ArrowLeft') to = at > 0 ? at - 1 : last;
    else if (key === 'Home') to = 0;
    else if (key === 'End') to = last;
    if (at < 0 || to < 0) return false;
    this.selectTab(tabs[to]);
    if (typeof tabs[to].focus === 'function') tabs[to].focus();
    return true;
  };

  PackageController.prototype.handleClick = function (target) {
    var hit = function (sel) {
      return target && target.closest ? target.closest(sel) : null;
    };
    var el;
    if ((el = hit('[data-pro-mode-tab]'))) return this.selectTab(el);
    if ((el = hit('[data-pro-wheel-mode]'))) return this.pickWheelMode(el.getAttribute('data-pro-wheel-mode'));
    if ((el = hit('[data-pro-finder-clear]'))) return this.clearContext();
    if ((el = hit('[data-pro-finder-retry]'))) return this.retry();
    if ((el = hit('[data-pro-package-fix]'))) return this.showDiameter(Number(el.getAttribute('data-pro-package-fix')));
    if ((el = hit('[data-pro-wheel-kind]'))) return this.chooseKind(el.getAttribute('data-pro-wheel-kind'));
    if ((el = hit('[data-pro-wheel-path]'))) return this.chooseWheelPath(el.getAttribute('data-pro-wheel-path'));
    if ((el = hit('[data-pro-wheel-diameter]'))) return this.chooseDiameter(Number(el.getAttribute('data-pro-wheel-diameter')));
    if ((el = hit('[data-pro-wheel-pcd]'))) return this.choosePcd(el.getAttribute('data-pro-wheel-pcd'));
    if ((el = hit('[data-pro-wheel-find-fitments]'))) return this.findVehicleFitments();
    if ((el = hit('[data-pro-wheel-fitment]'))) return this.chooseVehicleFitment(Number(el.getAttribute('data-pro-wheel-fitment')));
    if ((el = hit('[data-pro-find-tires]'))) return this.findTires();
    if ((el = hit('[data-pro-package-remove]'))) return this.removeFromPackage(el.getAttribute('data-pro-package-remove'));
    if ((el = hit('[data-pro-package-add]'))) {
      var quote = null;
      try {
        quote = JSON.parse(el.getAttribute('data-pro-package-quote'));
      } catch (e) {
        quote = null;
      }
      if (quote) return this.addToPackage(el.getAttribute('data-pro-package-add'), quote);
    }
    return null;
  };

  /** A changed Year / Make / Model / Trim list on the Wheels tab. */
  PackageController.prototype.handleChange = function (target) {
    var field = target && target.getAttribute && target.getAttribute('data-pro-wheel-vehicle-field');
    return field ? this.chooseVehicleField(field) : null;
  };

  /** Runs after assets/toee-pro.js (both deferred, in source order), so the
   * search form already carries its ProSearch instance (initAll). */
  function initPackageController() {
    if (!document.querySelector || !document.querySelector('[data-pro-package-bar]')) return;
    var form = document.querySelector('[data-pro-search]');
    var pro = typeof window !== 'undefined' ? window.ToeePro : null;
    var controller = new PackageController(document, {
      search: form && form._proSearch,
      proxyBase: pro && pro.proProxyUrl ? pro.proProxyUrl('') : '/apps/pro',
      fetchImpl: typeof fetch !== 'undefined' ? fetch.bind(typeof window !== 'undefined' ? window : undefined) : null,
      vehicle: function () { return pro && pro.activeVehicleController ? pro.activeVehicleController() : null; },
      wheelVehicle: pro && pro.createVehicleController ? pro.createVehicleController() : null
    });
    document.addEventListener('click', function (e) {
      controller.handleClick(e.target);
    });
    document.addEventListener('keydown', function (e) {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (controller.handleKey(e.target, e.key)) e.preventDefault();
    });
    document.addEventListener('change', function (e) {
      controller.handleChange(e.target);
    });
    document.addEventListener('focusin', function (e) {
      controller.handleFocus(e.target, true);
    });
    document.addEventListener('focusout', function (e) {
      controller.handleFocus(e.target, false, e.relatedTarget);
    });
    if (typeof window !== 'undefined' && window.addEventListener) {
      // The tray's height changes with the viewport (a rotation re-wraps its names).
      var remeasure = function () { controller.measureTray(); };
      window.addEventListener('resize', remeasure);
      window.addEventListener('orientationchange', remeasure);
    }
    if (form) form._proPackage = controller;
  }

  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initPackageController);
    } else {
      initPackageController();
    }
  }

  return {
    parseTireSize: parseTireSize,
    rimWidthRangeIn: rimWidthRangeIn,
    fitmentRimsUrl: fitmentRimsUrl,
    fitmentTiresUrl: fitmentTiresUrl,
    fitmentFromRims: fitmentFromRims,
    fitmentFromTires: fitmentFromTires,
    emptyPackage: emptyPackage,
    setPackageSide: setPackageSide,
    removePackageSide: removePackageSide,
    packageConflicts: packageConflicts,
    packageReady: packageReady,
    buildPackageQuote: buildPackageQuote,
    formatPcd: formatPcd,
    formatInches: formatInches,
    PackageController: PackageController
  };
});
